import { useState, useEffect } from 'react';
import { FaTimes, FaPause } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import logo from '../Assets/Images/YOUR-PACK_YP-G-COLOR.webp';
import './ModelWelcomeStory.css';

// Tiempo que dura la bienvenida si nadie la toca (el texto es largo, da para leerlo).
const DURATION_MS = 25000;

const FEATURES = [
  { icon: '📸', text: 'Puedes subir packs de fotos y videos y venderlos a los usuarios que estén interesados en tu contenido.' },
  { icon: '💬', text: 'También contamos con un chat privado, donde puedes hablar directamente con tus usuarios y ofrecerles contenido personalizado que no esté publicado en tus packs.' },
  { icon: '🎁', text: 'Además, los usuarios pueden enviarte regalos por el chat, que se convierten automáticamente en dinero para ti.' },
];

// Bienvenida a pantalla completa para el registro de modelo, con barra de tiempo
// arriba como las historias de Telegram: mantener presionado pausa, al soltar
// sigue, y al terminar (o al tocar la X o "Ir al registro") llama a onFinish.
export default function ModelWelcomeStory({ onFinish }) {
  const [holding, setHolding] = useState(false);
  const [tabHidden, setTabHidden] = useState(typeof document !== 'undefined' && document.hidden);

  // Si la pestaña queda en segundo plano, la historia no avanza.
  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const paused = holding || tabHidden;
  const hold = () => setHolding(true);
  const release = () => setHolding(false);

  return (
    <div
      className="fixed inset-0 z-[10100] bg-gradient-to-b from-rose-500 to-rose-800 text-white select-none"
      onPointerDown={hold}
      onPointerUp={release}
      onPointerLeave={release}
      onPointerCancel={release}
      onContextMenu={e => e.preventDefault()}
    >
      <div className="max-w-md mx-auto h-full flex flex-col px-4 pt-3 pb-4">
        {/* Barra de tiempo */}
        <div className="h-1 rounded-full bg-white/30 overflow-hidden">
          <div
            className="welcome-story__progress h-full bg-white rounded-full"
            style={{ animationDuration: `${DURATION_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }}
            onAnimationEnd={onFinish}
          />
        </div>

        {/* Encabezado */}
        <div className="flex items-center gap-2 mt-3">
          <img src={logo} alt="" className="w-9 h-9 rounded-full bg-white p-1 object-contain" draggable={false} />
          <span className="font-semibold text-sm">YourPack</span>
          {holding && (
            <span className="inline-flex items-center gap-1 text-xs text-white/80">
              <FaPause className="text-[10px]" /> En pausa
            </span>
          )}
          <button
            onPointerDown={e => e.stopPropagation()}
            onClick={onFinish}
            aria-label="Cerrar"
            className="ml-auto w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-white/10"
          >
            <FaTimes />
          </button>
        </div>

        {/* Mensaje */}
        <div className="flex-1 overflow-y-auto flex flex-col py-3">
          <div className="my-auto space-y-3">
          <div>
            <h1 className="text-xl font-semibold leading-tight">💕 ¡Hola! Bienvenida a YourPack</h1>
            <p className="mt-1 text-sm text-white/90">
              Nos alegra mucho que estés interesada en formar parte de nuestra plataforma 😊
            </p>
          </div>

          <p className="text-sm text-white/90">
            YourPack es una plataforma de contenido para adultos, similar a OnlyFans, donde puedes ganar dinero
            compartiendo contenido exclusivo con tus seguidores.
          </p>

          <div className="space-y-1.5">
            {FEATURES.map(f => (
              <div key={f.icon} className="flex gap-2.5 items-start rounded-xl bg-white/10 px-3 py-2">
                <span className="text-xl leading-none mt-0.5">{f.icon}</span>
                <p className="text-[13px] leading-snug">{f.text}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl bg-white text-rose-600 px-3 py-2.5 shadow-sm">
            <p className="font-semibold">🎉 Beneficio de bienvenida</p>
            <p className="text-[13px] leading-snug text-gray-700 mt-0.5">
              Durante tu primer mes, por cada 5 packs que publiques, recibirás una bonificación
              de <strong className="text-rose-600">$20 USD</strong>.
            </p>
          </div>

          <p className="text-sm text-white/90">
            Si deseas comenzar, podemos ayudarte con tu registro y explicarte cómo utilizar la plataforma
            paso a paso. ❤️
          </p>
          <p className="font-semibold">¡Estamos para ayudarte! 💕</p>
          </div>
        </div>

        {/* Pie fijo: para quien lee rápido y no quiere esperar a que termine la barra */}
        <div className="pt-2 space-y-2">
          <p className="text-center text-xs text-white/60">Mantén presionado para pausar</p>
          <button
            onPointerDown={e => e.stopPropagation()}
            onClick={onFinish}
            className="w-full bg-white text-rose-600 hover:bg-rose-50 font-semibold rounded-xl py-3 shadow-sm transition-colors"
          >
            Ir al registro
          </button>
        </div>
      </div>
    </div>
  );
}

// Aviso que aparece al terminar la bienvenida, antes de mostrar el formulario.
export function GoogleSignupHint({ darkMode, onClose }) {
  return (
    <div className="fixed inset-0 z-[10100] flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <div
        className={`w-full max-w-sm rounded-2xl shadow-xl p-6 text-center ${darkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-800'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <FcGoogle className="text-3xl" />
        </div>
        <h2 className="text-lg font-semibold mb-3">¿Cómo quieres registrarte?</h2>
        <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Si tienes <strong>Android</strong>, puedes registrarte rápido y seguro presionando el botón
          de <strong>Google</strong> en la parte inferior.
        </p>
        <p className={`text-sm mb-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Si quieres registrarte con un correo diferente, usa el método tradicional de <strong>correo y contraseña</strong>.
        </p>
        <button
          onClick={onClose}
          className="w-full text-white bg-rose-500 hover:bg-rose-700 font-semibold rounded-full text-sm px-5 py-2.5 transition-colors"
        >
          Entendido
        </button>
      </div>
    </div>
  );
}
